// @owners { team: patients-team }
import { type DeliveryMethodType } from '@alto/deliver_api/types/delivery_methods/v1/delivery_method_type';
import { Experimentation } from '@alto/experimentation';
import {
  CheckoutEndpoint,
  type CheckoutEndpointFetchFulfillmentOptionsRequest,
  type CheckoutEndpointFetchFulfillmentWindowOptionsRequest,
  type CheckoutEndpointFetchMailRequestRequiredRequest,
} from '@alto/scriptdash/alto/patient_app/checkout/v1/checkout_endpoint';
import { type Shipment } from '@alto/scriptdash/alto/patient_app/order_success/types/v1/shipment';
import { type PrescriptionSelectedPriceType } from '@alto/scriptdash/alto/patient_app/types/v1/prescription_selected_price_type';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMemo } from 'react';
import { apiEndpointHandler } from '~shared/helpers/api';
import { get } from '~shared/helpers/apiHelper';
import { formatWindowTimeLong } from '~shared/helpers/date';
import { useQuery } from '~shared/react-query';
import { formatDeliveryWindow } from '../helpers/formatDeliveryWindow';

const checkoutEndpoint = CheckoutEndpoint(apiEndpointHandler);

export const checkout = createQueryKeys('checkout', {
  fulfillmentOptions: (params: CheckoutEndpointFetchFulfillmentOptionsRequest) => ({
    queryKey: ['fulfillmentOptions', params],
    queryFn: () => checkoutEndpoint.fetchFulfillmentOptions(params),
  }),
  fulfillmentWindowOptions: (params: CheckoutEndpointFetchFulfillmentWindowOptionsRequest) => ({
    queryKey: ['fulfillmentWindowOptions', params],
    queryFn: () => checkoutEndpoint.fetchFulfillmentWindowOptions(params),
  }),
  orderSuccess: (params: { shipmentIDs: number[] }) => ({
    queryKey: ['orderSuccess', params],
    queryFn: async (): Promise<{ data: { shipments: Shipment[] } }> =>
      await get('/order_success', params, { version: 'v1' }),
  }),
  mailRequestRequired: (params: CheckoutEndpointFetchMailRequestRequiredRequest) => ({
    queryKey: ['mailRequestRequired', params],
    queryFn: () => checkoutEndpoint.fetchMailRequestRequired(params),
  }),
});

export const useQueryFulfillmentOptions = ({
  addressID,
  cartPrescriptionIDs,
  selectedPrices,
  shipmentID,
}: {
  addressID?: number;
  cartPrescriptionIDs: number[];
  selectedPrices?: PrescriptionSelectedPriceType[] | null | undefined;
  shipmentID?: number | null | undefined;
}) => {
  const { isSuccess, isLoading, isError, error, data } = useQuery({
    ...checkout.fulfillmentOptions({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      address_id: addressID!,
      cart_prescription_ids: cartPrescriptionIDs,
      selected_prices: selectedPrices,
      shipment_id: shipmentID,
    }),
    // Enable the query if we have an addressID and either:
    // 1. We have cart prescription IDs (new order), OR
    // 2. We have a shipmentID (editing existing order - backend will use prescription IDs from shipment)
    enabled: !!addressID && (cartPrescriptionIDs.length > 0 || !!shipmentID),
  });

  const courierDateFulfillmentOptions = useMemo(() => {
    return (data?.data?.courier_date_fulfillment_options || []).map((fulfillmentOption) => {
      return formatDeliveryWindow(fulfillmentOption);
    });
  }, [data?.data?.courier_date_fulfillment_options]);

  const courierAnyDateFulfillmentOption = useMemo(() => {
    return data?.data?.courier_any_date_fulfillment_option
      ? formatDeliveryWindow(data?.data?.courier_any_date_fulfillment_option)
      : null;
  }, [data?.data?.courier_any_date_fulfillment_option]);

  const pickupFulfillmentOptions = useMemo(() => {
    return (data?.data?.pickup_fulfillment_options || []).map((fulfillmentOption) => {
      return formatDeliveryWindow(fulfillmentOption);
    });
  }, [data?.data?.pickup_fulfillment_options]);

  const mailFulfillmentOptions = useMemo(() => {
    return (data?.data?.mail_fulfillment_options || []).map((fulfillmentOption) => {
      return formatDeliveryWindow(fulfillmentOption);
    });
  }, [data?.data?.mail_fulfillment_options]);

  const mailAnyDateFulfillmentOption = useMemo(() => {
    return data?.data?.mail_any_date_fulfillment_option
      ? formatDeliveryWindow(data?.data?.mail_any_date_fulfillment_option)
      : null;
  }, [data?.data?.mail_any_date_fulfillment_option]);

  return {
    isSuccess,
    isLoading,
    isError,
    error,
    courierDateFulfillmentOptions,
    courierAnyDateFulfillmentOption,
    pickupFulfillmentOptions,
    mailFulfillmentOptions,
    mailAnyDateFulfillmentOption,
  };
};

export const useQueryFulfillmentWindowOptions = ({
  addressID,
  cartPrescriptionIDs,
  date,
  selectedPrices,
  shipmentID,
}: {
  addressID?: number;
  cartPrescriptionIDs: number[];
  date: string;
  selectedPrices?: PrescriptionSelectedPriceType[] | null | undefined;
  shipmentID?: number | null | undefined;
}) => {
  const { isSuccess, isLoading, isError, error, data } = useQuery({
    ...checkout.fulfillmentWindowOptions({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      address_id: addressID!,
      cart_prescription_ids: cartPrescriptionIDs,
      date,
      selected_prices: selectedPrices,
      shipment_id: shipmentID,
    }),
    enabled: !!addressID,
  });

  const fulfillmentWindowOptions = useMemo(() => {
    return (data?.data?.fulfillment_window_options || []).map((fulfillmentWindowOption) => {
      if (fulfillmentWindowOption.delivery_window) {
        const {
          delivery_window: { start_time, end_time, cutoff_time },
          ...rest
        } = fulfillmentWindowOption;
        return {
          ...rest,
          delivery_window: {
            start_time: formatWindowTimeLong(start_time, 'a'),
            end_time: formatWindowTimeLong(end_time, 'a'),
            cutoff_time: cutoff_time ? formatWindowTimeLong(cutoff_time, 'a') : null,
          },
        };
      }
      return fulfillmentWindowOption;
    });
  }, [data?.data?.fulfillment_window_options]);

  return {
    isSuccess,
    isLoading,
    isError,
    error,
    fulfillmentWindowOptions,
  };
};

export const useOrderSuccess = ({ shipmentIDs }: { shipmentIDs: number[] }) => {
  const { isLoading, data, refetch } = useQuery({
    ...checkout.orderSuccess({
      shipmentIDs,
    }),
    enabled: shipmentIDs.length > 0,
  });

  return {
    isLoading,
    refetch,
    shipments: data?.data.shipments,
  };
};

export const useQueryMailRequestRequired = ({
  addressID,
  prescriptionIDs,
  deliveryMethod,
  shipmentID,
  isOnCompoundCheckoutPage = false,
  isLastShipment = true,
}: {
  addressID?: number;
  prescriptionIDs: number[];
  deliveryMethod?: DeliveryMethodType;
  shipmentID?: number | null | undefined;
  isOnCompoundCheckoutPage?: boolean;
  isLastShipment?: boolean;
}) => {
  const { value: mailRequestEnabled } = Experimentation.useFeatureFlag('mail_request');

  const { isSuccess, isLoading, isError, error, data } = useQuery({
    ...checkout.mailRequestRequired({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      address_id: addressID!,
      prescription_ids: prescriptionIDs,
      shipment_id: shipmentID,
    }),
    // Only make the API call if the mail request feature flag is enabled, has prescriptions,
    // an address ID is present, the delivery method is not 'pickup', and it’s the non-compound last shipment
    enabled:
      mailRequestEnabled &&
      !!addressID &&
      prescriptionIDs.length > 0 &&
      deliveryMethod !== 'pickup' &&
      !isOnCompoundCheckoutPage &&
      isLastShipment,
  });

  return {
    isSuccess,
    isLoading,
    isError,
    error,
    mailRequestRequired: data?.data?.mail_request_required || false,
    mailRequestRequiredMessage: data?.data?.mail_request_required_message || null,
  };
};
